import { useMemo, Context, useState, Dispatch, SetStateAction } from 'react';
import {
	ApisFormContextType,
	ErrorApisObject,
	FormDataApis,
	HandleSubmitCallback,
	ItemList,
	ListOptions,
	ModelApisForm
} from '../ApisForm.model';
import { getOptions, ApisFormContext, optionsUseForm, getRequiredFields, isValidDoc } from '../ApisForm.utils';
import { UseFormReturn, useForm } from 'react-hook-form';
import { DocOtherFields, DocVisaFields, RequiredFieldsType } from '../ApisForm.constants';

export type CountryCodes = {
	code: string;
	description: string;
	countryCode: string;
};

type UseApisFormProp = {
	countryCodes: ItemList[];
	listNationalities: ItemList[];
	listCountries: ItemList[];
	listStates: ItemList[];
	model: ModelApisForm;
	onSubmit: HandleSubmitCallback;
};
type UseApisFormReturn = {
	options: ListOptions;
	ApisFormContext: Context<ApisFormContextType>;
	methods: UseFormReturn<FormDataApis, undefined>;
	isFormDisabled: boolean;
	apisErrors?: ErrorApisObject[];
	requiredFields: RequiredFieldsType;
	isSubmitDisabled: boolean;
	showAlertErrors: boolean;
	setShowAlertErrors: Dispatch<SetStateAction<boolean>>;
	setIsSubmitDisabled: Dispatch<SetStateAction<boolean>>;
	setIsFormDisabled: Dispatch<SetStateAction<boolean>>;
	onSubmitApisForm: (pasanger: FormDataApis) => Promise<void>;
};

const checkDocument = (methods: UseFormReturn<FormDataApis>, pasanger: FormDataApis) => {
	// Check VISA
	const { watch, resetField } = methods;
	const docVisa = watch(DocVisaFields.MODEL);
	if (docVisa && !isValidDoc(docVisa)) {
		pasanger.documentationRequired.visa = undefined;

		resetField(DocVisaFields.EXPIRY_DATE, { defaultValue: '' });
		resetField(DocVisaFields.ISSUER_COUNTRY, { defaultValue: '' });
		resetField(DocVisaFields.NUMBER, undefined);
	}

	// Check Other
	const docOther = watch(DocOtherFields.MODEL);
	if (docOther && !isValidDoc(docOther)) {
		pasanger.documentationRequired.other = undefined;
	}
};

export const useApisForm = ({
	listNationalities,
	listCountries,
	listStates,
	countryCodes,
	onSubmit,
	model
}: UseApisFormProp): UseApisFormReturn => {
	const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
	const [isFormDisabled, setIsFormDisabled] = useState(false);
	const [showAlertErrors, setShowAlertErrors] = useState(false);
	const [apisErrors, setApisErrors] = useState<ErrorApisObject[]>([]);
	const { documentationRequired } = model;
	const mandatoryDocs = documentationRequired.mandatory.map((document) => document.type);

	const options: ListOptions = {
		nationalities: useMemo(() => getOptions(listNationalities), [listNationalities]),
		countries: useMemo(() => getOptions(listCountries), [listCountries]),
		states: useMemo(() => getOptions(listStates), [listStates]),
		mandatoryDocsTypes: getOptions(mandatoryDocs as ItemList[]),
		countryCode: useMemo(() => getOptions(countryCodes), [countryCodes])
	};

	const methods = useForm<FormDataApis>(optionsUseForm(model, countryCodes, listStates, listCountries));
	const requiredFields: RequiredFieldsType = getRequiredFields(model);

	const onSubmitApisForm = async (pasanger: FormDataApis) => {
		try {
			checkDocument(methods, pasanger);
			setApisErrors([]);
			await onSubmit(pasanger);
		} catch (error) {
			setApisErrors(Array.isArray(error) ? error : [error]);
		}
	};

	return {
		options,
		ApisFormContext,
		methods,
		isFormDisabled,
		requiredFields,
		isSubmitDisabled,
		apisErrors,
		showAlertErrors,
		setShowAlertErrors,
		setIsSubmitDisabled,
		setIsFormDisabled,
		onSubmitApisForm
	};
};
